import { Container } from 'src/components/common/Container'
import styled from 'styled-components'
import { Typography } from 'src/components/common/Typography'
import {
  Noto_body1_b16,
  Noto_body2_m14,
  Noto_body2_r14,
  Noto_heading3_b20,
} from 'src/styles/fonts/NotoSansKrFonts'
import { Divider } from 'src/components/common/Divider'
import { Roboto_num1_b24, Roboto_num2_b16 } from 'src/styles/fonts/RobotoFonts'
import { FreeCouponDetailPageProps } from 'src/pages/FreeCouponPage/FreeCouponDetailPage/index'
import { FreeCouponDetailSpace } from 'src/types/api'
import { DateUtils } from 'src/utils/date'
import React from 'react'
import { CouponDetailCard } from 'src/components/common/CouponDetailCardItems'
import Skeleton from 'react-loading-skeleton'
import CouponCircleImage from 'src/components/common/CouponCircleImage'
import { ellip } from 'src/styles/mixin'
import MixedHtmlData from 'src/components/common/MixedHtmlData'
import { Barcode } from 'src/components/common/Barcode'
import { Icon } from 'src/assets/svg/Icon'
import useHtml2CanvasDown from 'src/hooks/useHtml2CanvasDown.hook'
import { useToast } from 'src/provider/ToastContextProvider'
import { useChannel } from 'src/provider/ChannelContextProvider'
import DownloadCouponLayout from 'src/pages/DownloadCouponPage/_components/DownloadCouponLayout'
import RenderIf from 'src/components/common/RenderIf'
import FreeCouponDetailBottomSheet from 'src/pages/FreeCouponPage/_components/FreeCouponDetailBottomSheet'
import usePartnerEntry from 'src/hooks/usePartnerEntry.hook'
import LazyImage from 'src/components/common/LazyImage'
import bannerImage from 'src/assets/images/free_coupon_detail/free_coupon_detail_bottom_sheet_banner_01.png'
import usePageNav from 'src/hooks/usePageNav.hook'

const FreeCouponDetailPageView = ({
  freeCouponDetailData,
  isBottomSheetOpen,
  setIsBottomSheetOpen,
}: FreeCouponDetailPageProps) => {
  const { DateFormat } = DateUtils
  const {
    ExpireDT,
    RegDT,
    UseFlag,
    Barcode: barcode,
    Introduction,
    CouponImage,
    CouponName,
    RegularPrice,
    PinNumber,
  } = freeCouponDetailData as FreeCouponDetailSpace.Response

  const freeCouponDetailDataList = [
    { text: '발급일', value: DateFormat(RegDT, 'YYYY년 MM월 DD일') },
    { text: '유효기간', value: DateFormat(ExpireDT, 'YYYY년 MM월 DD일') },
    ...(PinNumber?.trim() ? [{ text: '쿠폰인증번호', value: PinNumber.trim() }] : []),
    { text: '사용여부', value: UseFlag === 1 ? '사용' : '미사용' },
  ]

  const { handleDownloadClick, handleShareClick, componentRef } = useHtml2CanvasDown()
  const { openToast } = useToast()
  const { web } = useChannel()
  const {
    value: { pCode },
  } = usePartnerEntry()
  const { mainPageNav } = usePageNav()

  const handleImgDownLoad = () =>
    handleDownloadClick(() => {
      web && openToast({ contents: '이미지 저장 완료' })
      setTimeout(() => {
        !isBottomSheetOpen && setIsBottomSheetOpen(true)
      }, 1000)
    })

  return (
    <>
      <FreeCouponDetailBottomSheet isOpen={isBottomSheetOpen} setIsOpen={setIsBottomSheetOpen} />
      <S.Container $option="flex_column">
        <CouponDetailCard.Bg>
          <CouponDetailCard.Container $barcode={true}>
            <CouponDetailCard.Top>
              <CouponCircleImage src={CouponImage} margin="0 auto 14px auto" />
              <S.CouponRegularPrice as={!!RegularPrice ? 'span' : Skeleton}>
                {RegularPrice?.toLocaleString()}
              </S.CouponRegularPrice>
              <S.CouponNameContainer $option="flex_column">
                <S.CouponName as={!!CouponName ? 'span' : Skeleton}>{CouponName}</S.CouponName>
              </S.CouponNameContainer>
            </CouponDetailCard.Top>
            <CouponDetailCard.Bottom>
              <Barcode.Image value={`${barcode}`} barcode_height={74} />
              <Barcode.Text value={`${barcode}`} fontStyle={Roboto_num2_b16} />
            </CouponDetailCard.Bottom>
          </CouponDetailCard.Container>
        </CouponDetailCard.Bg>
        <S.TopButtonWrap $color="var(--black-4)">
          <RenderIf when={web}>
            <S.TextButton onClick={handleShareClick}>
              <Typography $fontStyle={Noto_body1_b16} $color="var(--black-90)">
                공유하기
              </Typography>
            </S.TextButton>
          </RenderIf>
          <S.TextButton
            onClick={handleImgDownLoad}
            disabled={!barcode}
            className={!!pCode ? 'btn_freecp_save' : ''}
          >
            <Typography $fontStyle={Noto_body1_b16} $color="var(--black-90)">
              저장
            </Typography>
            <Icon.DownLoad color="var(--black-90)" />
          </S.TextButton>
        </S.TopButtonWrap>
        <CouponDetailCard.DetailInfoList>
          {freeCouponDetailDataList?.map((ele: { text: string; value: string }, index: number) => (
            <CouponDetailCard.DetailInfoLine
              key={index}
              as={ele?.value !== '' || !!ele?.value ? 'div' : Skeleton}
            >
              <CouponDetailCard.DetailTitle>{ele.text}</CouponDetailCard.DetailTitle>
              <CouponDetailCard.DetailDesc>{ele.value}</CouponDetailCard.DetailDesc>
            </CouponDetailCard.DetailInfoLine>
          ))}
        </CouponDetailCard.DetailInfoList>
        <RenderIf when={!pCode}>
          <Divider />
        </RenderIf>
        <RenderIf when={!!pCode}>
          <S.BannerContainer className="img_hotdeal" onClick={mainPageNav}>
            <LazyImage src={bannerImage} alt="핫딜 홍보 이미지" />
          </S.BannerContainer>
        </RenderIf>
        <S.FreeCouponInfoUseContainer $option="flex_column">
          <Typography $fontStyle={Noto_body1_b16}>이용안내</Typography>
          <Typography $fontStyle={Noto_body2_r14}>
            <MixedHtmlData html={Introduction} />
          </Typography>
        </S.FreeCouponInfoUseContainer>
        {/*다운로드*/}
        <DownloadCouponLayout
          couponDetailData={freeCouponDetailData!}
          componentRef={componentRef}
          barcode={freeCouponDetailData?.Barcode!}
        />
      </S.Container>
    </>
  )
}
export default FreeCouponDetailPageView

const S = {
  Container: styled(Container)`
    position: relative;
    background-color: var(--white);
  `,
  BannerContainer: styled(Container)`
    width: 100%;
    height: 100%;
    cursor: pointer;

    &.img_hotdeal * {
      pointer-events: none;
    }
  `,
  FreeCouponInfoUseContainer: styled(Container)`
    padding: 20px;
    gap: 10px;
  `,
  CouponRegularPrice: styled.span`
    ${Roboto_num1_b24};
    display: flex;
    margin-bottom: 6px;
    justify-content: center;
    align-items: center;
    width: 100%;
    color: var(--brand-50);
    &:after {
      content: '원';
      ${Noto_body2_m14};
      color: var(--black-50);
    }
  `,
  CouponNameContainer: styled(Container)`
    justify-content: center;
    height: 64px;
    align-items: center;
  `,
  CouponName: styled.span`
    ${Noto_heading3_b20};
    ${ellip('line2')};
  `,
  TopButtonWrap: styled(Container)<{ $color: string }>`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    padding: 17px 12px;
    background-color: ${({ $color }) => $color};
  `,
  TextButton: styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3px;

    &:not(:last-child) {
      border-right: 1px solid var(--black-30);
    }

    &.btn_freecp_save {
      & > svg,
      & > svg > path,
      & > span {
        pointer-events: none;
      }
    }
  `,
}
